import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, PhoneCall, Clock, TrendingUp, Calendar, MessageSquare, CheckCircle2, BarChart3, Users, Bot, Phone, Mic } from "lucide-react";

function FloatingHero() {
  return (
    <div className="relative w-full h-[460px] lg:h-[520px]" data-testid="hero-floating-elements">
      {/* Background decorative elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/40 via-blue-50/10 to-transparent rounded-full" />

      {/* Decorative circles */}
      <div className="absolute top-[15%] left-[8%] w-3 h-3 rounded-full bg-blue-200/40" style={{ animation: "heroPulse 4s ease-in-out infinite" }} />
      <div className="absolute top-[72%] right-[12%] w-2 h-2 rounded-full bg-teal-200/50" style={{ animation: "heroPulse 5s ease-in-out infinite 1s" }} />
      <div className="absolute top-[30%] right-[5%] w-2.5 h-2.5 rounded-full bg-blue-300/30" style={{ animation: "heroPulse 4.5s ease-in-out infinite 0.5s" }} />
      <div className="absolute bottom-[20%] left-[15%] w-2 h-2 rounded-full bg-indigo-200/40" style={{ animation: "heroPulse 5.5s ease-in-out infinite 2s" }} />

      {/* Decorative lines */}
      <div className="absolute top-[25%] right-[20%] w-10 h-[1px] bg-gradient-to-r from-transparent via-blue-200/40 to-transparent" style={{ animation: "heroFadeLine 6s ease-in-out infinite" }} />
      <div className="absolute bottom-[35%] left-[5%] w-8 h-[1px] bg-gradient-to-r from-transparent via-slate-200/50 to-transparent" style={{ animation: "heroFadeLine 5s ease-in-out infinite 1.5s" }} />

      {/* === PHONE — center piece === */}
      <div
        className="absolute left-1/2 top-[46%] -translate-x-[55%] -translate-y-1/2 z-30"
        style={{ animation: "heroPhoneFloat 6s ease-in-out infinite" }}
      >
        <div className="w-[152px] rounded-[26px] bg-gradient-to-b from-[#0F172A] to-[#1a2540] p-[6px] shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)]">
          {/* Notch */}
          <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-14 h-[3px] bg-black/60 rounded-full z-10" />
          <div className="bg-white rounded-[22px] overflow-hidden">
            {/* Status bar */}
            <div className="bg-[#3B82F6] px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-[18px] h-[18px] rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Phone className="w-2.5 h-2.5 text-white" strokeWidth={2} />
                </div>
                <div>
                  <span className="text-[8px] font-semibold text-white block leading-tight">Incoming Call</span>
                  <span className="text-[7px] text-white/70">AI Receptionist</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-green-300 animate-pulse" />
                <span className="text-[7px] text-white/80">Live</span>
              </div>
            </div>
            {/* Chat messages */}
            <div className="p-2 space-y-1.5 bg-gradient-to-b from-slate-50 to-white">
              <div className="max-w-[85%]">
                <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/10 rounded-xl rounded-tl-sm px-2.5 py-1.5">
                  <p className="text-[8px] text-slate-700 leading-[1.4]">Hello! Thanks for calling. How can I help you today?</p>
                </div>
              </div>
              <div className="max-w-[80%] ml-auto">
                <div className="bg-[#0F172A] rounded-xl rounded-tr-sm px-2.5 py-1.5">
                  <p className="text-[8px] text-white leading-[1.4]">I need a haircut tomorrow</p>
                </div>
              </div>
              <div className="max-w-[88%]">
                <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/10 rounded-xl rounded-tl-sm px-2.5 py-1.5">
                  <p className="text-[8px] text-slate-700 leading-[1.4]">I have <span className="font-semibold">2:30 PM</span> and <span className="font-semibold">4:00 PM</span> available. Which works?</p>
                </div>
              </div>
              <div className="max-w-[60%] ml-auto">
                <div className="bg-[#0F172A] rounded-xl rounded-tr-sm px-2.5 py-1.5">
                  <p className="text-[8px] text-white leading-[1.4]">2:30 please!</p>
                </div>
              </div>
            </div>
            {/* Input bar */}
            <div className="px-2 pb-2">
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1.5">
                <div className="w-4 h-4 rounded-full bg-[#3B82F6] flex items-center justify-center">
                  <Mic className="w-2 h-2 text-white" strokeWidth={2} />
                </div>
                <span className="text-[7px] text-slate-300 flex-1">Listening...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === DASHBOARD — top right === */}
      <div
        className="absolute right-0 lg:right-2 top-0 z-20"
        style={{ animation: "heroDashFloat 7s ease-in-out infinite" }}
      >
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-[0_8px_30px_-10px_rgba(15,23,42,0.12)] p-3.5 w-[175px]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-[#3B82F6]" strokeWidth={1.5} />
              <span className="text-[10px] font-semibold text-[#0F172A]">Dashboard</span>
            </div>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-slate-400">Calls Today</span>
              <div className="flex items-baseline gap-1">
                <span className="font-heading font-bold text-base text-[#0F172A]">47</span>
                <span className="text-[8px] text-green-600 font-medium">+12%</span>
              </div>
            </div>
            <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#3B82F6] to-blue-400 rounded-full" style={{ width: "94%" }} />
            </div>
            <div className="grid grid-cols-2 gap-1.5 pt-0.5">
              <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                <span className="text-[8px] text-slate-400 block">Leads</span>
                <span className="font-heading font-bold text-sm text-[#3B82F6]">32</span>
              </div>
              <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                <span className="text-[8px] text-slate-400 block">Booked</span>
                <span className="font-heading font-bold text-sm text-green-600">18</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === CALENDAR — left side === */}
      <div
        className="absolute left-0 lg:left-1 top-[12%] z-20"
        style={{ animation: "heroCalFloat 6.5s ease-in-out infinite" }}
      >
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-[0_8px_30px_-10px_rgba(15,23,42,0.1)] p-3.5 w-[155px]">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Calendar className="w-3.5 h-3.5 text-[#3B82F6]" strokeWidth={1.5} />
            <span className="text-[10px] font-semibold text-[#0F172A]">Tomorrow</span>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {[
              { t: "9:00", a: true },
              { t: "10:30", a: false },
              { t: "2:30", a: true, sel: true },
              { t: "3:00", a: true },
              { t: "4:00", a: true },
              { t: "5:30", a: false },
            ].map((s) => (
              <div key={s.t} className={`text-center py-1.5 rounded-lg text-[8px] font-medium transition-all ${
                s.sel
                  ? "bg-[#3B82F6] text-white shadow-sm shadow-blue-200"
                  : s.a
                  ? "bg-slate-50 text-slate-600 border border-slate-100"
                  : "bg-slate-50 text-slate-300 line-through"
              }`}>
                {s.t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === CHAT BUBBLE — mid left === */}
      <div
        className="absolute left-4 lg:left-6 bottom-[18%] z-20"
        style={{ animation: "heroChatFloat 5.8s ease-in-out infinite" }}
      >
        <div className="bg-white/90 backdrop-blur-md rounded-2xl border border-slate-200/80 shadow-[0_8px_30px_-10px_rgba(15,23,42,0.1)] p-3 w-[158px]">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="w-5 h-5 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
              <MessageSquare className="w-3 h-3 text-[#3B82F6]" strokeWidth={1.5} />
            </div>
            <span className="text-[10px] font-semibold text-[#0F172A]">Lead Captured</span>
          </div>
          <div className="space-y-1">
            {[
              { l: "Name", v: "Anna K." },
              { l: "Phone", v: "+1 555-239" },
              { l: "Service", v: "Haircut" },
            ].map((r) => (
              <div key={r.l} className="flex justify-between items-center">
                <span className="text-[8px] text-slate-400">{r.l}</span>
                <span className="text-[9px] font-medium text-[#0F172A]">{r.v}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 h-[1px] bg-slate-100" />
          <div className="mt-1.5 flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-[7px] text-green-600 font-medium">Saved to CRM</span>
          </div>
        </div>
      </div>

      {/* === APPOINTMENT CONFIRMED — bottom right === */}
      <div
        className="absolute right-2 lg:right-4 bottom-[8%] z-20"
        style={{ animation: "heroConfirmFloat 6s ease-in-out infinite" }}
      >
        <div className="bg-gradient-to-br from-green-50 to-emerald-50/80 backdrop-blur-md rounded-2xl border border-green-200/80 shadow-[0_8px_30px_-10px_rgba(34,197,94,0.15)] p-3.5 w-[178px]">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-sm shadow-green-200">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
            <div>
              <span className="text-[10px] font-semibold text-green-800 block leading-tight">Appointment Booked</span>
              <span className="text-[8px] text-green-600">Just now</span>
            </div>
          </div>
          <div className="bg-white/60 rounded-lg p-2 space-y-0.5">
            <p className="text-[9px] text-slate-600"><span className="text-slate-400">Client:</span> <span className="font-medium text-[#0F172A]">Anna K.</span></p>
            <p className="text-[9px] text-slate-600"><span className="text-slate-400">When:</span> <span className="font-medium text-[#0F172A]">Tomorrow, 2:30 PM</span></p>
            <p className="text-[9px] text-slate-600"><span className="text-slate-400">Service:</span> <span className="font-medium text-[#0F172A]">Haircut</span></p>
          </div>
        </div>
      </div>

      {/* === SMALL FLOATING PILLS === */}
      <div
        className="absolute right-[35%] top-[8%] z-10"
        style={{ animation: "heroPillFloat 8s ease-in-out infinite" }}
      >
        <div className="bg-white/80 backdrop-blur-sm rounded-full border border-slate-200/60 shadow-sm px-2.5 py-1 flex items-center gap-1.5">
          <Users className="w-2.5 h-2.5 text-[#3B82F6]" strokeWidth={1.5} />
          <span className="text-[8px] font-medium text-slate-500">32 leads today</span>
        </div>
      </div>

      <div
        className="absolute left-[30%] bottom-[5%] z-10"
        style={{ animation: "heroPillFloat2 7s ease-in-out infinite 2s" }}
      >
        <div className="bg-white/80 backdrop-blur-sm rounded-full border border-slate-200/60 shadow-sm px-2.5 py-1 flex items-center gap-1.5">
          <Bot className="w-2.5 h-2.5 text-teal-500" strokeWidth={1.5} />
          <span className="text-[8px] font-medium text-slate-500">AI active</span>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const scrollToForm = () => {
    const el = document.getElementById("application-form");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/60 via-white to-white" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/30 via-transparent to-transparent" />

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left: Copy */}
          <div className="animate-fade-in-up">
            <Badge
              variant="secondary"
              className="mb-6 bg-blue-50 text-[#3B82F6] border border-blue-200 hover:bg-blue-100 font-medium px-4 py-1.5 text-xs uppercase tracking-wide"
              data-testid="hero-badge"
            >
              AI-Powered Receptionist
            </Badge>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.1] text-balance mb-6">
              AI Receptionist for{" "}
              <span className="text-[#3B82F6]">Small Businesses</span>
            </h1>

            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-lg mb-8">
              Never miss a call or lead again. Our AI voice and chat receptionist answers customer questions, captures leads, checks availability, and books appointments automatically.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                onClick={scrollToForm}
                className="bg-[#0F172A] text-white h-12 px-8 rounded-lg font-medium hover:bg-[#1E293B] transition-all shadow-sm hover:shadow-md group"
                data-testid="hero-cta-button"
              >
                Book a Demo
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const el = document.getElementById("how-it-works");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="h-12 px-8 rounded-lg font-medium border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-[#3B82F6] hover:border-[#3B82F6]"
                data-testid="hero-demo-button"
              >
                See How It Works
              </Button>
            </div>

            <div className="flex flex-wrap gap-6">
              {[
                { icon: PhoneCall, label: "24/7 Call Handling" },
                { icon: Clock, label: "Instant Response" },
                { icon: TrendingUp, label: "More Booked Appointments" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-slate-500">
                  <Icon className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.5} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Floating UI Elements */}
          <div className="animate-fade-in-up hidden md:block" style={{ animationDelay: "0.15s" }}>
            <FloatingHero />
          </div>
        </div>
      </div>
    </section>
  );
}
